import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../styles/AssignCandidates.scss";
import {
  getCandidatesByRequisition,
  unassignCandidate,
  getAllStatuses,
  updateCandidateStatus,
  base_URL,
} from "../services/api";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

import { FaEye } from "react-icons/fa";

const STATUS_FLOW_CONFIG = {
  Submitted: { nextStatuses: [1, 2], label: "Submitted" },
  1: { nextStatuses: [3], label: "Internal Screen Select" },
  2: { nextStatuses: [], label: "Internal Screen Reject" },
  3: { nextStatuses: [4, 5, 6], label: "Sent to Client" },
  4: { nextStatuses: [7], label: "Client Screen Select" },
  5: { nextStatuses: [], label: "Client Screen Reject" },
  6: { nextStatuses: [], label: "Requirement Hold" },
  7: { nextStatuses: [8, 9, 10, 11, 12], label: "L1 Scheduled" },
  8: { nextStatuses: [], label: "L1 Feedback Pending" },
  9: { nextStatuses: [13], label: "L1-Select" },
  10: { nextStatuses: [], label: "L1-Reject" },
  11: { nextStatuses: [], label: "No Show" },
  12: { nextStatuses: [], label: "Requirement Hold" },
  13: { nextStatuses: [14, 15, 16, 17, 18], label: "L2 Scheduled" },
  14: { nextStatuses: [], label: "L2 Feedback Pending" },
  15: { nextStatuses: [19], label: "L2-Select" },
  16: { nextStatuses: [], label: "L2-Reject" },
  17: { nextStatuses: [], label: "No Show" },
  18: { nextStatuses: [], label: "Requirement Hold" },
  19: { nextStatuses: [20, 21, 22, 23, 24], label: "Client Round Scheduled" },
  20: { nextStatuses: [], label: "Client Round Feedback Pending" },
  21: { nextStatuses: [25], label: "Client Round-Select" },
  22: { nextStatuses: [], label: "Client Round-Reject" },
  23: { nextStatuses: [], label: "Client Round-Hold" },
  24: { nextStatuses: [], label: "Client Round-No Show" },
  25: { nextStatuses: [26], label: "HR Discussion" },
  26: { nextStatuses: [27], label: "BGV Inprogress" },
  27: { nextStatuses: [28], label: "Offer Inprogress" },
  28: { nextStatuses: [29, 30], label: "Offer Released" },
  29: { nextStatuses: [31, 32], label: "Offer Accepted" },
  30: { nextStatuses: [], label: "Offer Declined" },
  31: { nextStatuses: [], label: "Joined" },
  32: { nextStatuses: [], label: "Joining No Show" },
};

const useStatusFlow = (statusOptions) => {
  // Expensive calculations (e.g., large list filtering, sorting, formatting).
  // Memoizing derived state.
  // Avoiding unnecessary recalculations of values passed to child components.
  return useMemo(() => {
    if (!statusOptions?.length) return {};

    // Create ID to name mapping
    const idToName = {};
    const nameToId = {};

    statusOptions.forEach((status) => {
      idToName[status.id] = status.value;
      nameToId[status.value] = status.id;
    });

    return { idToName, nameToId };
  }, [statusOptions]);
};

function AssignedList() {
  const [loading, setLoading] = useState(false);
  const roleId = localStorage.getItem("role_id");
  const navigate = useNavigate();
  const location = useLocation();
  const [candidatesData, setCandidatesData] = useState([]);
  const [allCandidatesData, setAllCandidatesData] = useState([]);
  const [searchName, setSearchName] = useState("");

  const [remarks, setRemarks] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalCandidate, setStatusModalCandidate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  const { idToName, nameToId } = useStatusFlow(statusOptions);

  // Get requisition_id from navigation state
  const requisition_id = location.state?.requisition_id;
  // console.log(requisition_id)

  const fetchAssignedCandidatesData = async () => {
    try {
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });

      const response = await getCandidatesByRequisition({ requisition_id });
      Swal.close();

      if (
        response &&
        response.result_code === 200 &&
        Array.isArray(response.result_info)
      ) {
        setAllCandidatesData(response.result_info); // Store all candidates
        setCandidatesData(response.result_info); // Initially show all candidates
      } else {
        setCandidatesData([]);
        setAllCandidatesData([]);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setCandidatesData([]);
      setAllCandidatesData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignedCandidatesData();
  }, []);

  // Effect to filter candidates based on search input
  useEffect(() => {
    if (searchName.trim() === "") {
      setCandidatesData(allCandidatesData); // Show all candidates when search is empty
    } else {
      const filteredCandidates = allCandidatesData.filter((candidate) => {
        const searchTerm = searchName.toLowerCase();
        
        // Search in candidate name
        const nameMatch = candidate.candidate?.candidate_name
          ?.toLowerCase()
          .includes(searchTerm);
        
        // Search in assigned date
        const dateMatch = candidate.assigned_at && 
          new Date(candidate.assigned_at)
            .toLocaleDateString("en-GB")
            .split("/")
            .join("-")
            .toLowerCase()
            .includes(searchTerm);
        
        // Search in status
        const statusMatch = candidate.status?.toLowerCase().includes(searchTerm);

        if (roleId === "4") {
          // For recruiters, search in candidate name and assigned date only
          return nameMatch || dateMatch;
        } else {
          // For other roles (Admin, Hiring Manager), search in all fields
          return nameMatch || dateMatch || statusMatch;
        }
      });
      setCandidatesData(filteredCandidates);
    }
  }, [searchName, allCandidatesData, roleId]);

  useEffect(() => {
    fetchRequisitionDetails();
  }, []);

  const handleUnAssignCandidate = async (candidate) => {
    const userId = localStorage.getItem("user_id");

    // Only handle unassign operation
    const payload = {
      requisition_id: requisition_id,
      candidate_id: candidate?.candidate?.id,
      updated_by: userId ? parseInt(userId) : 1,
    };

    try {
      // Show loading
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
        // title: "Unassigning candidate...",
      });

      const res = await unassignCandidate(payload);
      Swal.close();

      if (res && res.status === "S") {
        // Refresh the data after successful unassignment
        await fetchAssignedCandidatesData();
        Swal.fire({
          icon: "success",
          title: "Candidate is now rejected",
          confirmButtonColor: "#0078b9",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: res?.message || "Unassignment failed!",
          confirmButtonColor: "#0078b9",
        });
      }
    } catch (err) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Unassignment failed!",
        confirmButtonColor: "#0078b9",
      });
    }
  };

  const openStatusModal = async (candidate) => {
    setStatusModalCandidate(candidate);
    setSelectedStatus(candidate?.status || "");
    setRemarks("");
    setStatusModalOpen(true);

    // Fetch statuses when modal opens
    try {
      const statusResponse = await getAllStatuses();
      if (
        statusResponse &&
        statusResponse.status === "S" &&
        Array.isArray(statusResponse.result_info)
      ) {
        const statuses = statusResponse.result_info.map((status) => ({
          value: status.name,
          label: status.name,
          id: status.id,
        }));
        setStatusOptions(statuses);
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) {
      Swal.fire({ icon: "warning", title: "Please select a status." });
      return;
    }
    const userId = localStorage.getItem("user_id");

    // Validate requisition_id
    if (!requisition_id) {
      Swal.fire({
        icon: "error",
        title: "Invalid requisition ID. Please try again.",
        confirmButtonColor: "#0078b9",
      });
      return;
    }

    const payload = {
      requisition_id: requisition_id,
      candidate_id: statusModalCandidate?.candidate?.id,
      new_status: selectedStatus,
      updated_by: userId ? parseInt(userId) : 1,
    };

    console.log("Update Status Payload:", payload);

    try {
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });
      const res = await updateCandidateStatus(payload);
      Swal.close();

      if (res && res.result_code === 200) {
        setStatusModalOpen(false);

        // Refresh the data after successful status update
        await fetchAssignedCandidatesData();

        // Show success message
        Swal.fire({
          icon: "success",
          title: "Status updated successfully!",
          confirmButtonColor: "#0078b9",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: res?.error_info || "Status update failed!",
          confirmButtonColor: "#0078b9",
        });
      }
    } catch (err) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Status update failed!",
        confirmButtonColor: "#0078b9",
      });
    }
  };
/* if (!statusOptions?.length || !currentStatusName) return [];

      // Find current status ID by name
      const currentStatusId = nameToId[currentStatusName] || "Submitted";
      const currentStatusConfig = STATUS_FLOW_CONFIG[currentStatusId];

      if (!currentStatusConfig) return [];

      // Get next available status IDs
      const nextStatusIds = currentStatusConfig.nextStatuses || [];

      // Create options array
      const options = [
        // Current status (disabled)
        {
          value: currentStatusName,
          label: currentStatusName,
          id: currentStatusId,
          isDisabled: true,
        },
      ];
      console.log("Current Status:", currentStatusName);
  console.log("Status ID:", currentStatusId);
  console.log("Next statuses:", nextStatusIds);

      // Add available next statuses
      nextStatusIds.forEach((statusId) => {
        const statusName = idToName[statusId];
        if (statusName) {
          options.push({
            value: statusName,
            label: statusName,
            id: statusId,
            isDisabled: false,
          });
        }
      });

      return options;
    },
    [statusOptions, idToName, nameToId]
  );*/
  const getAvailableStatusOptions = useCallback(
    (currentStatusName) => {
      if (!statusOptions?.length) return [];

      return statusOptions.map((status) => ({
        value: status.value,
        label: status.label,
        id: status.id,
        isDisabled: status.value === currentStatusName,
      }));
    },
    [statusOptions]
  );

  const [requisition, setRequisition] = useState(null);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const handleAccordionToggle = () => setAccordionOpen((open) => !open);

  const fetchRequisitionDetails = async () => {
    const requisitionState = location.state?.requisition || {};
    // console.log("Location state:", location.state);
    // console.log("Requisition state:", requisitionState);
    // console.log("Requisition ID from state:", location.state?.requisition_id);

    //  Fallback: Use requisition data from navigation state
    if (requisitionState && Object.keys(requisitionState).length > 0) {
      console.log("Using requisition data from state:", requisitionState);
      setRequisition({
        correl_id: requisitionState.correl_id || "",
        jobTitle:
          requisitionState.jobTitle || requisitionState.job_title || "-",
        location: requisitionState.location || "- ",
        department: requisitionState.department || "-",
        hiringManager:
          requisitionState.hiringManager || requisitionState.recruiters || "-",
        openings: requisitionState.openings || 0,
        description:
          requisitionState.description ||
          requisitionState.job_desc ||
          "No description available",
        skills: requisitionState.skills || [],
        candidatesCount: 0,
      });
    } else {
      console.warn(
        "No requisition data found in navigation state, using fallback"
      );
      //  Final fallback if no state passed
      setRequisition({
        jobTitle: "No Job Selected",
        location: "-",
        department: "-",
        hiringManager: "-",
        openings: 0,
        description: "No job requisition data available",
        skills: [],
        candidatesCount: 0,
      });
    }
  };

  // After all hooks and logic, do the conditional return
  if (!requisition) return <div>Loading...</div>;

  return (
    <div className="assign-candidates-container">
      <div className="assign-candidates-inner">
        <div className="requisition-accordion">
          <div className="accordion-summary" onClick={handleAccordionToggle}>
            <div>{requisition.jobTitle}</div>
            {/* Accordion */}
            <svg
              style={{
                transform: accordionOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
              width="20"
              height="20"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                d="M9 6l6 6-6 6"
                stroke="#0078b9"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {/* About Job Details */}
          {accordionOpen && (
            <div className="accordion-details">
              <div className="accordion-details-grid">
                <div>
                  <span className="accordion-details-label">Job Title:</span>{" "}
                  {requisition.jobTitle}
                </div>
                <div>
                  <span className="accordion-details-label">Department:</span>{" "}
                  {requisition.department}
                </div>
                <div>
                  <span className="accordion-details-label">Location:</span>{" "}
                  {requisition.location || "-"}
                </div>

                {/* <div>
                  <span className="accordion-details-label">Openings:</span>{" "}
                  {requisition.openings}
                </div> */}
                <div>
                  <span className="accordion-details-label">Skills:</span>{" "}
                  {requisition.skills &&
                  Array.isArray(requisition.skills) &&
                  requisition.skills.length > 0
                    ? requisition.skills.join(", ")
                    : "-"}
                </div>
              </div>
              {/* <div style={{ marginTop: "0.5rem" }}>
                <span className="accordion-details-label">Description:</span>{" "}
                {requisition.description}
              </div> */}
            </div>
          )}
          {/* Search by name */}
          <div
            className="assign-candidates-filters"
            style={{
              marginBottom: "1.5rem",
              padding: "0.5rem 0",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type="text"
                placeholder={
                  roleId === "4"
                    ? "Search candidates by Name or Date"
                    : "Search candidates by Name, Date, or Status"
                }
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                style={{
                  minWidth: 300,
                  padding: "0.75rem 1rem 0.75rem 2.5rem",
                  paddingRight: searchName.trim() !== "" ? "2.5rem" : "1rem",
                  border: "1.5px solid #d1d5db",
                  borderRadius: 8,
                  background: "#fff",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition:
                    "border-color 0.2s, box-shadow 0.2s, padding 0.2s",
                }}
                                
                onFocus={(e) => {
                  e.target.style.borderColor = "#0078b9";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 120, 185, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "none";
                }}
              />
              <svg
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  width: "16px",
                  height: "16px",
                  color: "#6b7280",
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>

              {/* Clear search button */}
              {searchName.trim() !== "" && (
                <button
                  onClick={() => setSearchName("")}
                  style={{
                    position: "absolute",
                    right: "0.75rem",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                  title="Clear search"
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: "#6b7280" }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* To display data in a table */}
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem 0",
                background: "#fff",
                borderRadius: 12,
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
              }}
            >
              <img
                src={require("../assets/loader.gif")}
                style={{ width: 50 }}
                alt="Loading..."
              />
              <div style={{ marginTop: "1rem", color: "#6b7280" }}>
                Loading candidate data...
              </div>
            </div>
          ) : (
            <div
              className="assign-candidates-table-wrapper"
              style={{
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                borderRadius: 12,
                background: "#fff",
              }}
            >
              <table
                className="assign-candidates-table assigned-list-table"
                style={{ width: "100%" }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th>Date</th>
                    <th>Candidate Name</th>
                    <th>Current Status</th>
                    <th>Resume</th>
                    {roleId !== "4" && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {candidatesData.map((candidateData) => (
                    <tr key={candidateData.id}>
                      <td>
                        {candidateData.updated_at
                          ? new Date(candidateData.updated_at)
                              .toLocaleDateString("en-GB")
                              .split("/")
                              .join("-")
                          : "-"}
                      </td>
                      <td>{candidateData?.candidate?.candidate_name || "-"}</td>
                      <td>{candidateData?.status || "-"}</td>
                      <td>
                        {candidateData?.candidate?.resume_path ? (
                          <a
                            href={`${
                              process.env.REACT_APP_API_URL || base_URL
                            }files/resume/${candidateData.candidate.resume_path.replace(
                              /\\/g,
                              "/"
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaEye />
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                       {roleId !== "4" && (
                      <td>
                        <div className="d-flex align-items-center">
                          <button
                            className="assign-btn"
                            style={{
                              padding: "0.4rem 1rem",
                              fontSize: "0.95rem",
                              minWidth: 120,
                              background: "#0078B9",
                              color: "#fff",
                              border: "1.5px solid #0078B9",
                              fontWeight: 600,
                              transition: "all 0.2s",
                              marginRight: "8px",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleUnAssignCandidate(candidateData);
                            }}
                          >
                            Reject
                          </button>
                          {/* Hide Update Status button for Recruiter (role_id 4) */}
                         
                            <button
                              className="assign-btn"
                              style={{
                                padding: "0.4rem 1rem",
                                fontSize: "0.95rem",
                                minWidth: 120,
                                background: "#f1f5f9",
                                color: "#0078b9",
                                border: "1.5px solid #0078b9",
                                fontWeight: 600,
                                transition: "all 0.2s",
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openStatusModal(candidateData);
                              }}
                            >
                              Update Status
                            </button>
                          
                        </div>
                        
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {candidatesData.length === 0 && !loading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "3rem 0",
                    color: "#6b7280",
                    fontSize: "1rem",
                  }}
                >
                  {searchName.trim() !== ""
                    ? `No candidates found matching "${searchName}"`
                    : "No candidates assigned to this requisition."}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {statusModalOpen && roleId !== "4" && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="modal-content"
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 32,
              minWidth: 500,
              width: "500px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
            }}
          >
            <h3 style={{ marginBottom: 18, fontWeight: 600 }}>Update Status</h3>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ fontWeight: 500, marginBottom: 6, display: "block" }}
              >
                Select Status *
              </label>
              <Select
               // options={statusOptions}
                 options={getAvailableStatusOptions(
                   statusModalCandidate?.status
                 )}
               // options={statusOptions}
                onMenuOpen={() => {
                  console.log(
                    "Modal opened with candidate:",
                    statusModalCandidate
                  );
                  console.log(
                    "Status being passed:",
                    statusModalCandidate?.status
                  );
                }}
                value={
                  selectedStatus
                    ? { value: selectedStatus, label: selectedStatus }
                    : null
                }
                onChange={(selected) =>
                  setSelectedStatus(selected ? selected.value : "")
                }
                placeholder="Select status"
                isSearchable={true}
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 6,
                    border: "1.5px solid #d1d5db",
                    minHeight: "42px",
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#6b7280",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "#374151",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isDisabled
                      ? "#f3f4f6"
                      : state.isFocused
                      ? "#e5e7eb"
                      : "white",
                    color: state.isDisabled ? "#9ca3af" : "#374151",
                    cursor: state.isDisabled ? "not-allowed" : "default",
                  }),
                }}
                isOptionDisabled={(option) => option.isDisabled}
              />
            </div>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}
            >
              <button
                style={{
                  padding: "0.5rem 1.2rem",
                  background: "#e5e7eb",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
                onClick={() => setStatusModalOpen(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: "0.5rem 1.2rem",
                  background: "#0078b9",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
                onClick={handleUpdateStatus}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssignedList;
